import { CUSTOMER_RETURN_CONFIG } from '@/config/customerReturn.config'

/**
 * Tips Drawer Composable
 *
 * Provides reactive state for the tips drawer component. Controls visibility
 * and tip content — rendering is handled by AppReturnTipsDrawer.vue.
 *
 * @example
 * // In a component or composable that controls the drawer:
 * const { showTipsDrawer, hideTipsDrawer } = useTipsDrawer()
 * showTipsDrawer()  // Shows drawer with random tip
 * hideTipsDrawer()  // Hides the drawer
 *
 * @example
 * // In the TipsDrawer component to bind reactive state:
 * const { visible, currentTip } = useTipsDrawer()
 *
 * @example
 * // With custom tip
 * showTipsDrawer('Custom tip message here')
 *
 * @example
 * // Override global config to disable tips drawer for this instance
 * const { showTipsDrawer, hideTipsDrawer } = useTipsDrawer({ enabled: false })
 */

// ============================================================================
// TIPS POOL
// ============================================================================
export const TIPS_POOL = [
  "When your item can move forward to a new buyer, that's the route Peg takes — fewer miles, faster resolution.",
  "A return doesn't have to mean a long trip back. When Peg can move your item forward instead, it will.",
  'Track your return status anytime right here in the Peg app.',
  'Returning more than one item? Peg walks you through each one — it only takes a moment per item.',
  'Once your return is approved, Peg keeps you posted at every step. No wondering, no waiting on hold.',
  "Peg handles the back-and-forth so you don't have to chase anyone down.",
  'When your item is eligible, Peg matches it with a new buyer nearby — no trip back to a warehouse needed.',
  "Peg figures out the best path for your return before anything ships. That's why it's faster.",
  'Every return is different, so Peg looks at your specific item and picks the best way to handle it.',
  'Good photos help Peg find your item its next home faster.',
  'Natural light is your friend — it makes photos clearer and approvals faster.',
  'Show any tags, labels, or accessories in your photos. It helps.',
  'Still have the original box? Great — mention it. Buyers love original packaging.',
  'A few clear photos now can mean a quicker approval in a minute.',
  "About 1 in 5 online purchases gets returned. You're in good company.",
  '79% of shoppers expect free return shipping. Peg agrees.',
  'Most people check the return policy before they buy — smart move.',
]

/**
 * Get a random tip from the tips pool
 * @returns {string} Random tip message
 */
const getRandomTip = () => {
  const randomIndex = Math.floor(Math.random() * TIPS_POOL.length)
  return TIPS_POOL[randomIndex]
}

// ============================================================================
// SHARED REACTIVE STATE
// ============================================================================
const visible = ref(false)
const currentTip = ref('')

// ============================================================================
// PUBLIC API
// ============================================================================

export default function useTipsDrawer({ enabled = CUSTOMER_RETURN_CONFIG.tipsDrawer.ENABLED, sessionStore = null } = {}) {
  /**
   * Show the tips drawer with a random or custom tip
   * @param {string} [customTip] - Optional custom tip to display instead of random tip
   */
  function showTipsDrawer(customTip) {
    if (!enabled) return

    currentTip.value = customTip || (sessionStore?.getNextTip ? sessionStore.getNextTip() : getRandomTip())
    visible.value = true
  }

  /**
   * Hide the tips drawer
   */
  function hideTipsDrawer() {
    if (!enabled) return

    visible.value = false
  }

  return {
    visible: readonly(visible),
    currentTip: readonly(currentTip),
    showTipsDrawer,
    hideTipsDrawer,
  }
}
